import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import config from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, DollarSign, Briefcase, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';

const AppliedJobs = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, [profile]);

  const fetchApplications = async () => {
    if (!profile) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/applications/user', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load your applications');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold mb-2">My Applications</h1>
          <p className="text-muted-foreground">Track the status of your job applications</p>
        </div>

        {applications.length === 0 ? (
          <Card className="p-6 sm:p-12 text-center">
            <Briefcase className="mx-auto mb-4 text-muted-foreground" size={48} />
            <h3 className="font-heading text-xl font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground mb-4">Start applying to jobs to see them here</p>
            <Button onClick={() => navigate('/jobs')} className="rounded-xl">Browse Jobs</Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {applications.map((app) => (
              <Card
                key={app.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/jobs/${app.job_id}`)}
                data-testid="application-card"
              >
                <CardContent className="p-5">
                  <div className="mb-3">
                    <h3 className="font-semibold text-lg mb-2">{app.job?.title || 'Job'}</h3>
                    <div className="flex items-center text-sm text-muted-foreground mb-2">
                      <MapPin size={14} className="mr-2" />
                      {app.job?.location || 'Location not specified'}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium">Status:</span>
                    <Badge
                      className={
                        app.status === 'accepted' || app.status === 'revealed'
                          ? 'bg-green-500'
                          : app.status === 'rejected'
                          ? 'bg-red-500'
                          : app.status === 'withdrawn'
                          ? 'bg-gray-500'
                          : ''
                      }
                    >
                      {app.status}
                    </Badge>
                  </div>

                  <div className="flex gap-2">
                    {/* Show chat button ONLY if revealed/accepted */}
                    {(app.status === 'accepted' || app.status === 'revealed') && app.job?.employer_id && (
                      <Button
                        size="sm"
                        className="flex-1 rounded-xl bg-green-600 hover:bg-green-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/messages?user=${app.job.employer_id}`);
                        }}
                      >
                        <MessageCircle size={14} className="mr-2" />
                        Chat with Employer
                      </Button>
                    )}
                    
                    {/* Show withdraw button ONLY if pending/applied */}
                    {(app.status === 'applied' || app.status === 'pending') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-xl"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { data: session } = await supabase.auth.getSession();
                            if (!session?.session?.access_token) {
                              toast.error('Please log in');
                              return;
                            }

                            const response = await fetch(
                              `${config.BACKEND_URL}/api/applications/${app.id}/withdraw`,
                              {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${session.session.access_token}`
                                }
                              }
                            );

                            if (!response.ok) {
                              // Clone response before reading body to prevent "body already used" error
                              const errorData = await response.clone().json();
                              throw new Error(errorData.detail || 'Failed to withdraw');
                            }
                            
                            toast.success('Application withdrawn');
                            
                            // Force immediate refetch
                            setTimeout(() => {
                              fetchApplications();
                            }, 100);
                          } catch (error) {
                            console.error('Withdraw error:', error);
                            toast.error(error.message || 'Failed to withdraw');
                          }
                        }}
                      >
                        Withdraw Application
                      </Button>
                    )}
                  </div>

                  <div className="mt-3 text-xs text-muted-foreground">
                    Applied: {new Date(app.created_at).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BottomNav profile={profile} />
    </div>
  );
};

export default AppliedJobs;
